import { useMemo, useState } from 'react';
import {
  type ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { RedactedEmail } from '@/components/redacted-email';
import { DataTable } from '@/components/super-admin/data-table';
import { userNameOrNull } from '@/lib/user-display-name';

interface Member {
  id: string;
  userId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  role: string;
  createdAt: string;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function roleBadgeVariant(role: string): 'default' | 'outline' | 'secondary' {
  switch (role.toLowerCase()) {
    case 'admin':
      return 'default';
    default:
      return 'secondary';
  }
}

export function WorkspaceMembersList({ members }: { members: Member[] }) {
  const [search, setSearch] = useState('');
  // Memoized: an unmemoized array remounts every cell on each render.
  const data = useMemo(() => members, [members]);

  const columns = useMemo<ColumnDef<Member>[]>(
    () => [
      {
        id: 'name',
        accessorFn: (m) => userNameOrNull(m) ?? '',
        header: 'Name',
        enableSorting: true,
        meta: { sortLabel: 'name' },
        cell: ({ row }) => {
          const name = userNameOrNull(row.original);
          if (name) return name;
          // Email fallback stays masked — never plain text.
          return row.original.email ? <RedactedEmail email={row.original.email} /> : '—';
        },
      },
      {
        id: 'email',
        // Non-null accessor: TanStack decides global-filter eligibility from the
        // first row's value, so a leading null would drop this column from search.
        accessorFn: (m) => m.email ?? '',
        header: 'Email',
        enableSorting: true,
        meta: { sortLabel: 'email' },
        cell: ({ row }) =>
          row.original.email ? <RedactedEmail email={row.original.email} /> : '—',
      },
      {
        accessorKey: 'role',
        header: 'Role',
        enableSorting: true,
        enableGlobalFilter: false,
        meta: { sortLabel: 'role' },
        cell: ({ row }) => (
          <Badge variant={roleBadgeVariant(row.original.role)}>
            {row.original.role.charAt(0).toUpperCase() + row.original.role.slice(1)}
          </Badge>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Joined',
        enableSorting: true,
        enableGlobalFilter: false,
        meta: { sortLabel: 'joined date' },
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    getRowId: (row) => row.id,
    defaultColumn: { enableSorting: false },
    enableMultiSort: false,
    sortDescFirst: false,
    state: { globalFilter: search },
    onGlobalFilterChange: setSearch,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (members.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <h3 className="text-xl font-semibold">No members</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          This workspace has no members.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Input
        placeholder="Search members by name or email..."
        aria-label="Search members"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-sm"
      />
      <div className="rounded-md border">
        <DataTable table={table} emptyState="No members match that search." />
      </div>
    </div>
  );
}
